/**
 * LoginPageLaunch — Login standalone para prueba SSO Launcher
 *
 * Ruta: /launch-login
 *
 * Este componente es completamente independiente del login existente.
 * Propósito: hacer login, guardar token + googleId en localStorage con
 * claves propias del launcher (launch_*), y redirigir a /launch.
 *
 * NO modifica ni interfiere con el flujo de login existente (/login).
 */

// libraries
import { useGoogleLogin, TokenResponse, GoogleOAuthProvider } from '@react-oauth/google';
import { Container, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

// assets
import { ImageHero } from '@assets/images';

// styles
import { WrapperLogin } from '../LoginPage/styles';

// components
import { LoginHeader, AutoLoginLoader } from '../LoginPage/components';

// ─────────────────────────────────────────────────────────────────────────────
// Claves exclusivas del launcher (para no pisar las del login normal)
// ─────────────────────────────────────────────────────────────────────────────
const LAUNCH_TOKEN_KEY     = 'launch_token';
const LAUNCH_GOOGLE_ID_KEY = 'launch_googleId';

const saveLaunchAuth = (token: string, googleId: string): void => {
  localStorage.setItem(LAUNCH_TOKEN_KEY, token);
  localStorage.setItem(LAUNCH_GOOGLE_ID_KEY, googleId);
  console.info('✅ [Launcher] Auth guardada en localStorage:', { token: '***', googleId });
};

// ─────────────────────────────────────────────────────────────────────────────
// Botón Google interno (no reutiliza ButtonGoogle para ser completamente aparte)
// ─────────────────────────────────────────────────────────────────────────────
interface InternalGoogleButtonProps {
  onSuccess: () => void;
  onError: () => void;
}

const InternalGoogleButton = ({ onSuccess, onError }: InternalGoogleButtonProps) => {
  const [loading, setLoading] = useState(false);

  const loginGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse: TokenResponse) => {
      try {
        setLoading(true);

        // 1. Guardar token inmediatamente (lo necesitamos para SSO)
        const token = tokenResponse.access_token;

        // 2. Obtener googleId del perfil
        const res     = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const profile = await res.json();

        // 3. Guardar en localStorage con claves del launcher
        saveLaunchAuth(token, profile.sub);

        onSuccess();
      } catch (err) {
        console.error('❌ [Launcher] Error en login:', err);
        setLoading(false);
        onError();
      }
    },
    onError: () => {
      onError();
    },
  });

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2">
        <Spinner animation="border" size="sm" />
        <span>Autenticando...</span>
      </div>
    );
  }

  return (
    <button
      onClick={() => loginGoogle()}
      style={{
        background: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px 32px',
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        transition: 'opacity 0.2s',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.85')}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
    >
      {/* Google icon */}
      <svg width="20" height="20" viewBox="0 0 24 24">
        <path fill="white" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="white" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="white" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path fill="white" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
      Ingresar con Google
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
const LoginPageLaunchInner = () => {
  const navigate   = useNavigate();
  const [error, setError]     = useState(false);
  const [logging, setLogging] = useState(false);

  const handleSuccess = () => {
    setLogging(true);
    setTimeout(() => navigate('/launch'), 400);
  };

  const handleError = () => {
    setError(true);
  };

  return (
    <Container fluid className="p-0">
      <div className="d-flex">
        <div className="p-0">
          <ImageHero style={{ height: '100vh' }} />
        </div>

        <div className="d-flex align-items-center justify-content-center w-100 mb-5">
          {logging ? (
            <AutoLoginLoader
              title="Preparando launcher..."
              subtitle="Guardando credenciales SSO"
            />
          ) : (
            <WrapperLogin className="mb-5">
              <div className="d-flex flex-column align-items-center gap-3 mb-5">
                <LoginHeader subtitle="SSO Launcher — Prueba" />
                <div className="google">
                  Inicia sesión para abrir <br /> otras herramientas con SSO
                </div>

                <InternalGoogleButton onSuccess={handleSuccess} onError={handleError} />

                {error && (
                  <div
                    style={{
                      background: '#fef2f2',
                      color: '#dc2626',
                      border: '1px solid #fca5a5',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '13px',
                      textAlign: 'center',
                    }}
                  >
                    ❌ Error al iniciar sesión. Intenta de nuevo.
                  </div>
                )}
              </div>
            </WrapperLogin>
          )}
        </div>
      </div>
    </Container>
  );
};

export const LoginPageLaunch = () => {
  const authKey = process.env.ENV_GOOGLE_AUTH;

  if (!authKey) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-danger text-center">
          <strong>Error:</strong> No se encontró ENV_GOOGLE_AUTH en las variables de entorno.
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={authKey}>
      <LoginPageLaunchInner />
    </GoogleOAuthProvider>
  );
};

